import React from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, Switch, Button, Space, Tag } from 'antd';
import { HolderOutlined, EditOutlined } from '@ant-design/icons';

const TYPE_LABELS = {
  'data-grid': 'Grilla',
  text: 'Texto',
  table: 'Tabla',
  images: 'Imágenes',
  signatures: 'Firmas',
};

function SortableSectionCard({ section, onToggleVisible, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card size="small" style={{ marginBottom: 8 }} styles={{ body: { padding: '8px 12px' } }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Button
              type="text"
              icon={<HolderOutlined />}
              {...attributes}
              {...listeners}
              style={{ cursor: 'grab' }}
              aria-label="Reordenar sección"
            />
            <div>
              <div style={{ fontWeight: 600 }}>{section.title || '(sin título)'}</div>
              <Tag color="green" style={{ marginTop: 4 }}>
                {TYPE_LABELS[section.type] || section.type}
              </Tag>
            </div>
          </Space>
          <Space>
            <Switch
              checked={section.visible !== false}
              onChange={(checked) => onToggleVisible(section.id, checked)}
              checkedChildren="Visible"
              unCheckedChildren="Oculta"
            />
            <Button type="primary" ghost icon={<EditOutlined />} onClick={() => onEdit(section)}>
              Editar
            </Button>
          </Space>
        </Space>
      </Card>
    </div>
  );
}

function SectionList({ sections, onReorder, onToggleVisible, onEdit }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sections.findIndex((s) => s.id === active.id);
    const newIndex = sections.findIndex((s) => s.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(sections, oldIndex, newIndex));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
        {sections.map((section) => (
          <SortableSectionCard
            key={section.id}
            section={section}
            onToggleVisible={onToggleVisible}
            onEdit={onEdit}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}

export default SectionList;
